/**
 * PythonNodeView — displays Python node details: logic description,
 * steps list, and validation results table.
 *
 * Used inside NodeToolEmbed when the node engine is 'python'.
 */

import React from 'react';
import { Code, CheckCircle, XCircle, Clock } from 'lucide-react';
import type { NodeDefinition, ModelRun, RunStatus } from '../../types/modeling';

interface PythonNodeViewProps {
  node: NodeDefinition;
  lastRun: ModelRun | null;
  activeRunProgress: ModelRun | null;
}

export default function PythonNodeView({ node, lastRun, activeRunProgress }: PythonNodeViewProps) {
  const run = activeRunProgress || lastRun;
  const nodeResult = run?.node_results?.find((nr) => nr.node_id === node.node_id);
  const validationResults = run?.validation_results?.filter((vr) =>
    vr.source_ids.includes(node.node_id),
  );

  return (
    <div className="h-full overflow-y-auto p-6 bg-bg-warm">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent-tint flex items-center justify-center">
            <Code className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-fg">{node.name}</h2>
            <p className="text-xs text-fg-tertiary">Python computation node</p>
          </div>
          {nodeResult && (
            <div className="ml-auto">
              <RunStatusBadge status={nodeResult.status} timeMs={nodeResult.execution_time_ms} />
            </div>
          )}
        </div>

        {/* Description */}
        {node.description && (
          <div className="bg-surface rounded-lg border border-border-default p-4">
            <h3 className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Description</h3>
            <p className="text-sm text-fg-secondary">{node.description}</p>
          </div>
        )}

        {/* Logic description */}
        {node.logic_description && (
          <div className="bg-surface rounded-lg border border-border-default p-4">
            <h3 className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Logic</h3>
            <p className="text-sm text-fg-secondary">{node.logic_description}</p>
          </div>
        )}

        {/* Logic steps */}
        {node.logic_steps.length > 0 && (
          <div className="bg-surface rounded-lg border border-border-default p-4">
            <h3 className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Steps</h3>
            <ol className="space-y-2">
              {node.logic_steps.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-bg-warm flex items-center justify-center text-xs font-semibold text-fg-tertiary">
                    {i + 1}
                  </span>
                  <span className="text-fg-secondary pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* I/O summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-surface rounded-lg border border-border-default p-4">
            <h3 className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Inputs</h3>
            {node.inputs.length === 0 ? (
              <p className="text-xs text-fg-tertiary">No inputs</p>
            ) : (
              <div className="space-y-1.5">
                {node.inputs.map((inp) => (
                  <div key={inp.artifact_name} className="text-xs">
                    <span className="font-medium text-fg-secondary">{inp.artifact_name}</span>
                    <span className="text-fg-tertiary ml-1">({inp.expected_type})</span>
                    {inp.required && <span className="text-danger ml-1">*</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-surface rounded-lg border border-border-default p-4">
            <h3 className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Outputs</h3>
            {node.outputs.length === 0 ? (
              <p className="text-xs text-fg-tertiary">No outputs</p>
            ) : (
              <div className="space-y-1.5">
                {node.outputs.map((out) => (
                  <div key={out.artifact.artifact_name} className="text-xs">
                    <span className="font-medium text-fg-secondary">{out.artifact.artifact_name}</span>
                    <span className="text-fg-tertiary ml-1">({out.artifact.artifact_type})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Validation results */}
        {validationResults && validationResults.length > 0 && (
          <div className="bg-surface rounded-lg border border-border-default p-4">
            <h3 className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Validation results</h3>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-divider">
                  <th className="text-left py-1.5 text-fg-tertiary font-medium">Check</th>
                  <th className="text-left py-1.5 text-fg-tertiary font-medium">Status</th>
                  <th className="text-left py-1.5 text-fg-tertiary font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {validationResults.map((vr) => (
                  <tr key={vr.check_id} className="border-b border-divider">
                    <td className="py-1.5 text-fg-secondary">{vr.check_name}</td>
                    <td className="py-1.5">
                      {vr.passed ? (
                        <span className="flex items-center gap-1 text-success">
                          <CheckCircle className="w-3 h-3" /> Pass
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-danger">
                          <XCircle className="w-3 h-3" /> Fail
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-fg-tertiary">{vr.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Error display */}
        {nodeResult?.error && (
          <div className="bg-danger-bg rounded-lg border border-danger p-4">
            <h3 className="text-xs font-semibold text-danger uppercase tracking-wide mb-1">Error</h3>
            <p className="text-sm text-danger font-mono">{nodeResult.error}</p>
            {nodeResult.error_detail && (
              <p className="text-xs text-danger mt-1">{nodeResult.error_detail}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RunStatusBadge({ status, timeMs }: { status: RunStatus; timeMs?: number }) {
  const config: Record<RunStatus, { bg: string; text: string; label: string }> = {
    pending: { bg: 'bg-bg-warm', text: 'text-fg-secondary', label: 'Pending' },
    running: { bg: 'bg-accent-tint', text: 'text-accent', label: 'Running' },
    completed: { bg: 'bg-success-bg', text: 'text-success', label: 'Completed' },
    failed: { bg: 'bg-danger-bg', text: 'text-danger', label: 'Failed' },
  };

  const c = config[status];

  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${c.bg} ${c.text} text-xs font-medium`}>
      {status === 'running' && <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />}
      {c.label}
      {timeMs !== undefined && (
        <span className="flex items-center gap-0.5 opacity-70">
          <Clock className="w-3 h-3" />
          {timeMs < 1000 ? `${timeMs}ms` : `${(timeMs / 1000).toFixed(1)}s`}
        </span>
      )}
    </div>
  );
}
